import { SelectProps } from "antd";
import {
  TemplatesTypeEnum,
  CampaignTypeEnum,
  ChannelEnum,
  RFMPeriod,
  StatusEnum,
  TypeEnum,
} from "./enums";
import { ChannelKey, StatusKey, TypeKey, RfmOption } from "./type";
import { ReactNode } from "react";

// Campaign Channel
export const ChannelMap: Record<ChannelKey, ChannelEnum> = {
  SMS: ChannelEnum.Sms,
  "WhatsApp Standard": ChannelEnum.WhatsAppStandard,
  "WhatsApp Premium": ChannelEnum.WhatsAppPremium,
  Email: ChannelEnum.Email,
};

export const ChannelKeyMap: Record<ChannelEnum, ChannelKey> = {
  [ChannelEnum.Sms]: "SMS",
  [ChannelEnum.WhatsAppStandard]: "WhatsApp Standard",
  [ChannelEnum.WhatsAppPremium]: "WhatsApp Premium",
  [ChannelEnum.Email]: "Email",
};

// Campaign Type
export const TypeMap: Record<TypeKey, TypeEnum> = {
  "One-Time": TypeEnum.Onetime,
  "Recurring - Weekly": TypeEnum.Recurring_Weekly,
  "Recurring - Monthly": TypeEnum.Recurring_Monthly,
};

export const TypeKeyMap: Record<TypeEnum, TypeKey> = {
  [TypeEnum.Onetime]: "One-Time",
  [TypeEnum.Recurring_Weekly]: "Recurring - Weekly",
  [TypeEnum.Recurring_Monthly]: "Recurring - Monthly",
};

// Campaign Status
export const StatusMap: Record<StatusKey, StatusEnum> = {
  Draft: StatusEnum.Draft,
  Active: StatusEnum.Active,
  Paused: StatusEnum.Paused,
  Completed: StatusEnum.Completed,
  Cancelled: StatusEnum.Cancelled,
};

export const StatusKeyMap: Record<StatusEnum, StatusKey> = {
  [StatusEnum.Draft]: "Draft",
  [StatusEnum.Active]: "Active",
  [StatusEnum.Paused]: "Paused",
  [StatusEnum.Completed]: "Completed",
  [StatusEnum.Cancelled]: "Cancelled",
};

export const RFM_LABELS = [
  "Lifetime",
  "Last 2 Years",
  "Last 1 Year",
  "Last 9 Months",
  "Last 6 Months",
  "Last 3 Months",
] as const;
export type RFMLabel = (typeof RFM_LABELS)[number];

export const labelToRFMTPeriodMap = {
  Lifetime: RFMPeriod.Lifetime,
  "Last 2 Years": RFMPeriod.Last2Years,
  "Last 1 Year": RFMPeriod.Last1Year,
  "Last 9 Months": RFMPeriod.Last9Months,
  "Last 6 Months": RFMPeriod.Last6Months,
  "Last 3 Months": RFMPeriod.Last3Months,
} as const satisfies Record<RFMLabel, RFMPeriod>;
export const rfmPeriodToLabel: Partial<Record<RFMPeriod, RFMLabel>> = {
  [RFMPeriod.Lifetime]: "Lifetime",
  [RFMPeriod.Last2Years]: "Last 2 Years",
  [RFMPeriod.Last1Year]: "Last 1 Year",
  [RFMPeriod.Last9Months]: "Last 9 Months",
  [RFMPeriod.Last6Months]: "Last 6 Months",
  [RFMPeriod.Last3Months]: "Last 3 Months",
  // CustomRange omitted on purpose
};
export const RfmPeriod: RfmOption[] = Object.entries(labelToRFMTPeriodMap).map(
  ([label, value]) => ({ label, value }),
);

export const STATUS_ENUM_VALUES: StatusEnum[] = (
  Object.values(StatusEnum) as Array<string | number>
).filter(
  (v) =>
    typeof v === "number" ||
    !Object.prototype.hasOwnProperty.call(StatusEnum, v),
) as StatusEnum[];

export const STATUS_OPTIONS = STATUS_ENUM_VALUES.map((s) => ({
  label: StatusKeyMap[s],
  value: StatusKeyMap[s],
}));

export const CampaignTypeLabel: Record<CampaignTypeEnum, string> = {
  [CampaignTypeEnum.Voucher]: "Voucher - purchases made using vouchers only",
  [CampaignTypeEnum.Point]: "Point - purchases made using points only",
  [CampaignTypeEnum.VoucherPoint]:
    "Voucher & Point - purchases made using either vouchers or points",
  [CampaignTypeEnum.EVoucher]:
    "E-Voucher - account specific E-Voucher purchases",
  [CampaignTypeEnum.Custom]: "Custom Campaign",
  [CampaignTypeEnum.ProfileCompletionReminder]: "Profile Completion Reminder",
};

export const CAMPAIGN_TYPE_OPTIONS: SelectProps["options"] = [
  {
    label: CampaignTypeLabel[CampaignTypeEnum.Voucher],
    value: CampaignTypeEnum.Voucher,
  },
  {
    label: CampaignTypeLabel[CampaignTypeEnum.Point],
    value: CampaignTypeEnum.Point,
  },
  {
    label: CampaignTypeLabel[CampaignTypeEnum.VoucherPoint],
    value: CampaignTypeEnum.VoucherPoint,
  },
  {
    label: CampaignTypeLabel[CampaignTypeEnum.EVoucher],
    value: CampaignTypeEnum.EVoucher,
  },
  {
    label: CampaignTypeLabel[CampaignTypeEnum.ProfileCompletionReminder],
    value: CampaignTypeEnum.ProfileCompletionReminder,
  },
  {
    label: CampaignTypeLabel[CampaignTypeEnum.Custom],
    value: CampaignTypeEnum.Custom,
  },
];

export const CAMPAIGN_TEMPLATES_OPTIONS: SelectProps["options"] = [
  {
    label: "Default Templates",
    value: TemplatesTypeEnum.Default,
  },
  {
    label: "Customize",
    value: TemplatesTypeEnum.Custom,
  },
  {
    label: "Uploaded Member List",
    value: TemplatesTypeEnum.UploadedMemberList,
  },
];

export const TEMPLATE_TYPE_DESCRIPTIONS: Record<TemplatesTypeEnum, string> = {
  [TemplatesTypeEnum.Default]:
    "This template uses a pre-configured RFM segmentation targeting members with no transactions or visits in the last {inactiveDays} days",
  [TemplatesTypeEnum.Custom]:
    "Fully customizable RFM segmentation. Select your own period, segments and criteria.",
  [TemplatesTypeEnum.UploadedMemberList]:
    "Upload a fixed target member list. No RFM segmentation is applied — only the uploaded members will receive this campaign.",
};

export function getTemplateLabel(
  value: TemplatesTypeEnum | undefined,
): ReactNode {
  const option = CAMPAIGN_TEMPLATES_OPTIONS?.find((opt) => opt.value === value);
  return option?.label || "-";
}

export type IncompleteProfileFieldOption = {
  label: string;
  value: string;
  defaultChecked?: boolean;
};

export const INCOMPLETE_PROFILE_FIELD_OPTIONS: IncompleteProfileFieldOption[] =
  [
    { label: "First Name", value: "FirstName", defaultChecked: true },
    { label: "Last Name", value: "LastName", defaultChecked: true },
    { label: "Birthday", value: "Birthday", defaultChecked: true },
    { label: "Country", value: "Country" },
    { label: "Postal Code", value: "PostalCode" },
    { label: "Address", value: "Address" },
    { label: "Gender", value: "Gender" },
  ];

const joinWithAnd = (items: string[]): string => {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
};

export const describeDefaultIncompleteProfileFields = (
  options: IncompleteProfileFieldOption[],
): string => {
  const defaultLabels = options
    .filter((f) => f.defaultChecked)
    .map((f) => f.label);
  if (defaultLabels.length === 0) return "";
  const verb = defaultLabels.length === 1 ? "is" : "are";
  return `${joinWithAnd(defaultLabels)} ${verb} selected by default, but you can deselect them.`;
};

// Recognizes the "birthday" field regardless of which key vocabulary it
// came from — the hardcoded fallback list uses "Birthday", the real
// GetDynamicSignUpForm API uses "DateOfBirth".
const BIRTHDAY_INCOMPLETE_PROFILE_FIELD_VALUES = new Set([
  "Birthday",
  "DateOfBirth",
]);
export const isBirthdayIncompleteProfileField = (value: string): boolean =>
  BIRTHDAY_INCOMPLETE_PROFILE_FIELD_VALUES.has(value);

const DYNAMIC_SIGNUP_FORM_IGNORED_KEYS = new Set([
  "Merchant",
  "EnableDynamicTemplate",
  "ConfirmPassword",
  "PromotionsPreferences",
  // Profile Completion Reminder only sends via SMS/WhatsApp, both of which
  // require a phone number already — a member missing it can't be reached,
  // so it's never a selectable "incomplete" field.
  "PhoneNumber",
]);

const DYNAMIC_SIGNUP_FORM_FIELD_LABELS: Record<string, string> = {
  FirstName: "First Name",
  LastName: "Last Name",
  PhoneNumber: "Phone Number",
  EmailAddress: "Email Address",
  DateOfBirth: "Birthday",
  AddressLine1: "Address Line 1",
  AddressLine2: "Address Line 2",
  PostalCode: "Postal Code",
  HubId: "Hub ID",
};

export const prettifyDynamicSignupFormKey = (key: string): string =>
  DYNAMIC_SIGNUP_FORM_FIELD_LABELS[key] ??
  key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").trim();

// "...Hide" / "...ViewName" keys are display metadata for the signup form
// itself, not selectable profile fields.
const isDynamicSignupFormMetaKey = (key: string): boolean =>
  DYNAMIC_SIGNUP_FORM_IGNORED_KEYS.has(key) ||
  /Hide$/.test(key) ||
  /ViewName$/.test(key);

// Fixed display order for known fields — the API response's key order
// doesn't match how they should read in the dropdown. Anything not listed
// here (e.g. new dynamic fields) is appended after, in response order.
const DYNAMIC_SIGNUP_FORM_FIELD_ORDER = [
  "FirstName",
  "LastName",
  "PhoneNumber",
  "EmailAddress",
  "Password",
  "DateOfBirth",
  "Gender",
  "AddressLine1",
  "AddressLine2",
  "PostalCode",
  "HubId",
];

const dynamicSignupFormOrderIndex = (key: string): number => {
  const idx = DYNAMIC_SIGNUP_FORM_FIELD_ORDER.indexOf(key);
  return idx === -1 ? DYNAMIC_SIGNUP_FORM_FIELD_ORDER.length : idx;
};

// Maps the /V1/RFM/GetDynamicSignUpForm response into "Incomplete Profile
// Fields" options for Profile Completion Reminder campaigns. Every field is
// a plain boolean — true means required, so it's selected by default. A
// field whose "{key}Hide" companion key is true is hidden on the merchant's
// signup form, so it's excluded from the options entirely.
export const mapDynamicSignupFormToIncompleteProfileFields = (
  result: Record<string, unknown> | null | undefined,
): IncompleteProfileFieldOption[] => {
  if (!result) return [];
  return Object.entries(result)
    .filter(
      ([key]) =>
        !isDynamicSignupFormMetaKey(key) && result[`${key}Hide`] !== true,
    )
    .map(([key, value]) => ({
      label: prettifyDynamicSignupFormKey(key),
      value: key,
      defaultChecked: value === true,
    }))
    .sort(
      (a, b) =>
        dynamicSignupFormOrderIndex(a.value) -
        dynamicSignupFormOrderIndex(b.value),
    );
};
